import type { ShellReminder } from '@web-app/components/Shell/shell-contribution';
import { useShellContributions } from '@web-app/components/Shell/ShellContributions';
import { Button } from '@web-app/components/ui/Button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@web-app/components/ui/Dialog/dialog';
import { useStoredCooldown } from '@web-app/hooks/utils/useStoredCooldown';
import { useEffect, useState } from 'react';

/** The first contributed reminder; it closes itself once the feature drops it. */
export function ShellReminderModal() {
  const reminder = useShellContributions().find(
    ({ reminder }) => reminder,
  )?.reminder;
  if (!reminder) return null;
  return <ReminderDialog reminder={reminder} />;
}

function ReminderDialog({ reminder }: { reminder: ShellReminder }) {
  const { canShow, recordShown } = useStoredCooldown(
    `shell-reminder:${reminder.id}:last-shown`,
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(
    function openOnceCooledDown() {
      if (!canShow || isOpen) return;
      recordShown();
      setIsOpen(true);
    },
    [canShow, isOpen, recordShown],
  );

  // Any close restarts the full cooldown, as "Remind Me Later" does.
  const close = () => {
    recordShown();
    setIsOpen(false);
  };
  const closeAnd = (onClick: () => void) => () => {
    close();
    onClick();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent className="max-w-[480px]">
        <span
          aria-hidden
          className="flex size-11 items-center justify-center rounded-full bg-destructive/10 text-destructive [&_svg]:size-[22px]"
        >
          {reminder.icon}
        </span>
        <div className="flex flex-col gap-1.5">
          <DialogTitle>{reminder.title}</DialogTitle>
          <DialogDescription className="text-[15px] leading-[23px] text-foreground">
            {reminder.description}
          </DialogDescription>
        </div>
        <ul className="flex max-h-64 flex-col overflow-y-auto rounded-[14px] border border-border">
          {reminder.items.map((item) => (
            <li
              key={item.id}
              className="border-b border-border last:border-b-0"
            >
              <button
                type="button"
                onClick={closeAnd(item.onClick)}
                className="flex w-full flex-col gap-0.5 px-3.5 py-2.5 text-left outline-none hover:bg-primary-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset"
              >
                <span className="text-sm font-semibold">{item.title}</span>
                <span className="text-[13px] leading-[18px] text-muted-foreground">
                  {item.meta}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>
            Remind Me Later
          </Button>
          <Button onClick={closeAnd(reminder.action.onClick)}>
            {reminder.action.label}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
